import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, Phone, ZoomIn } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import Lightbox from '../components/Lightbox';
import { useLanguage } from '../i18n/pageTranslations';

const ClickImg = ({ src, alt, caption, images, index, onOpen, className = "w-full h-auto" }) => (
  <div className="cursor-pointer group relative" onClick={() => onOpen(images, index)}>
    <img src={src} alt={alt} className={className} />
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
      <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} />
    </div>
    {caption && <p className="mt-3 text-text-secondary font-medium text-center">{caption}</p>}
  </div>
);

const CirugiaMamas = () => {
  const lang = useLanguage();
  const [lbIndex, setLbIndex] = useState(null);
  const [lbImages, setLbImages] = useState([]);

  const openLb = (images, index) => { setLbImages(images); setLbIndex(index); };

  const t = {
    es: {
      title: 'Cirugía Estética de Mamas',
      heroP1: '<strong>Embellecimiento del pecho</strong> con prótesis anatómicas de última generación para resultados naturales y armoniosos.',
      heroP2: 'Consulta personalizada para elegir la técnica y el tamaño que mejor se adapte a tu cuerpo.',
      cta: 'SOLICITAR CONSULTA',
      prosthesisTitle: 'Prótesis Anatómicas de Última Generación',
      naturalShape: 'Forma Natural', naturalShapeDesc: 'Imitan la forma de la mama natural con más volumen en la parte inferior.',
      cohesiveGel: 'Gel Cohesivo', cohesiveGelDesc: 'Relleno de silicona de alta cohesividad para mayor seguridad.',
      naturalTouch: 'Tacto Natural', naturalTouchDesc: 'Textura y consistencia muy similar al tejido mamario natural.',
      lifetimeWarranty: 'Garantía de por Vida', lifetimeWarrantyDesc: 'Prótesis de primeras marcas con garantía vitalicia.',
      typesTitle: 'Tipos de Cirugía de Mamas',
      augmentation: 'Aumento de Pecho', augmentationDesc: 'Incremento del volumen mamario mediante prótesis de silicona (anatómicas o redondas) para conseguir un pecho más proporcionado y natural.',
      lift: 'Elevación de Pecho (Mastopexia)', liftDesc: 'Corrección de la caída del pecho, elevando y remodelando la mama para conseguir un aspecto más juvenil y firme.',
      reduction: 'Reducción de Pecho', reductionDesc: 'Disminución del tamaño de las mamas en casos de hipertrofia mamaria, aliviando problemas de espalda y mejorando la proporción corporal.',
      expTitle: 'Experiencia en Cirugía Mamaria',
      expP1: 'El Dr. Ángel Norro cuenta con más de <strong>30 años de experiencia</strong> en cirugía estética de mamas, habiendo realizado miles de intervenciones.',
      expP2: 'Cada paciente recibe un <strong>estudio personalizado</strong> para elegir el tipo de prótesis, tamaño y técnica quirúrgica más adecuados.',
      expP3: 'El objetivo es conseguir un resultado <strong>natural y armonioso</strong> que mejore la silueta respetando las proporciones de cada cuerpo.',
      personalizedTitle: 'Resultados Personalizados',
      personalizedP1: 'Los resultados son siempre <strong>a gusto del paciente</strong> en cuanto a forma y volumen, siempre dentro de los límites de las posibilidades de cada paciente, cuyas circunstancias particulares se estudian en la <strong>consulta previa</strong>. Las imágenes de antes y después que mostramos corresponden a pacientes satisfechos con sus resultados, adaptados a su <strong> gusto personal</strong>.',
      personalizedP2: 'Trabajamos con <strong>más de 400 referencias de prótesis</strong> en cuanto a forma y tamaño, lo que nos permite encontrar la opción perfecta para cada paciente.',
      personalizedP3: 'En <strong>cirugía sin prótesis</strong>, podemos diseñar el pecho en forma y tamaño completamente a gusto del paciente, utilizando técnicas de remodelación con los propios tejidos.',
      case1Title: 'Resultados con Prótesis Anatómicas', case1Subtitle: 'Aumento de pecho con resultado natural',
      frontalView: 'Vista Frontal', lateralViews: 'Vistas Laterales', lateralView: 'Vista Lateral',
      before: 'Antes', after: 'Después',
      disclaimer: 'Caso real - Resultados pueden variar según cada paciente',
      case2Title: 'Elevación y Aumento de Pecho', case2Subtitle: 'Antes y a la semana en la revisión',
      oneWeek: '1 semana', revision: 'Revisión', twoWeeks: '2 semanas',
      case3Title: 'Reconstrucción tras Retirada de Prótesis',
      case3Desc: 'Reconstrucción estética tras retirada de prótesis antigua colocada en otro servicio veinte años antes. El pecho caía por delante de la prótesis encapsulada. La paciente no deseaba más volumen y la reconstrucción se llevó a cabo <strong>sin prótesis</strong>.',
      withEncapsulated: 'Con prótesis encapsulada', withoutProsthesis: 'Reconstrucción sin prótesis',
      case4Title: 'Corrección de Ptosis y Asimetría',
      case4Desc: 'Ptosis y asimetría tras lactancias. Corrección estética con mastopexia.',
      case4Note: 'Aspecto al mes de la cirugía donde se aprecian las cicatrices que en este periodo están enrojecidas.',
      oneMonthAfter: '1 mes después',
      case5Title: 'Aumento de Volumen Estético con Prótesis',
      case5Desc: 'Aumento mamario con prótesis para conseguir un volumen natural y armónico.',
      arrowBefore: '↑ Antes', arrowAfter: '↓ Después',
      case6Title: 'Mastopexia sin Prótesis',
      case6Desc: 'Corrección de ptosis mamaria utilizando los propios tejidos de la paciente, sin necesidad de prótesis.',
      case6Note: 'A los 3 meses se aprecian las cicatrices como unas finas líneas rojas que van desapareciendo poco a poco.',
      threeMonths: '3 meses después',
      case7Title: 'Mastopexia sin Prótesis - Recolocación de Tejidos',
      case7Desc: 'Técnica que recoloca los propios tejidos de la paciente para aportar el volumen apropiado, sin necesidad de implantes.',
      case7Note: 'Resultado natural utilizando exclusivamente los tejidos propios.',
      case9Title: 'Aumento Mamario - Caso 2',
      case9Desc: 'Planificación quirúrgica con marcaje preoperatorio y resultado final.',
      contactTitle: '¿Interesada en la Cirugía de Mamas?', contactDesc: 'Solicite una valoración personalizada',
    },
    en: {
      title: 'Aesthetic Breast Surgery',
      heroP1: '<strong>Breast enhancement</strong> with state-of-the-art anatomical implants for natural and harmonious results.',
      heroP2: 'Personalized consultation to choose the technique and size that best suits your body.',
      cta: 'REQUEST CONSULTATION',
      prosthesisTitle: 'State-of-the-Art Anatomical Implants',
      naturalShape: 'Natural Shape', naturalShapeDesc: 'They mimic the natural breast shape with more volume in the lower part.',
      cohesiveGel: 'Cohesive Gel', cohesiveGelDesc: 'High-cohesivity silicone filling for greater safety.',
      naturalTouch: 'Natural Touch', naturalTouchDesc: 'Texture and consistency very similar to natural breast tissue.',
      lifetimeWarranty: 'Lifetime Warranty', lifetimeWarrantyDesc: 'Top-brand implants with lifetime warranty.',
      typesTitle: 'Types of Breast Surgery',
      augmentation: 'Breast Augmentation', augmentationDesc: 'Increase in breast volume using silicone implants (anatomical or round) to achieve a more proportionate and natural breast.',
      lift: 'Breast Lift (Mastopexy)', liftDesc: 'Correction of breast sagging, lifting and reshaping the breast for a more youthful and firm appearance.',
      reduction: 'Breast Reduction', reductionDesc: 'Reduction of breast size in cases of breast hypertrophy, relieving back problems and improving body proportion.',
      expTitle: 'Experience in Breast Surgery',
      expP1: 'Dr. Ángel Norro has more than <strong>30 years of experience</strong> in aesthetic breast surgery, having performed thousands of procedures.',
      expP2: 'Each patient receives a <strong>personalized study</strong> to choose the most suitable type of implant, size and surgical technique.',
      expP3: 'The goal is to achieve a <strong>natural and harmonious</strong> result that improves the silhouette while respecting each body\'s proportions.',
      personalizedTitle: 'Personalized Results',
      personalizedP1: 'Results are always <strong>to the patient\'s liking</strong> in terms of shape and volume, always within each patient\'s possibilities, whose particular circumstances are studied in the <strong>prior consultation</strong>. The before and after images we show correspond to patients satisfied with their results, adapted to their <strong>personal taste</strong>.',
      personalizedP2: 'We work with <strong>more than 400 implant references</strong> in terms of shape and size, allowing us to find the perfect option for each patient.',
      personalizedP3: 'In <strong>surgery without implants</strong>, we can design the breast in shape and size entirely to the patient\'s liking, using reshaping techniques with the patient\'s own tissues.',
      case1Title: 'Results with Anatomical Implants', case1Subtitle: 'Breast augmentation with natural result',
      frontalView: 'Frontal View', lateralViews: 'Lateral Views', lateralView: 'Lateral View',
      before: 'Before', after: 'After',
      disclaimer: 'Real case - Results may vary for each patient',
      case2Title: 'Breast Lift and Augmentation', case2Subtitle: 'Before and one week at the follow-up',
      oneWeek: '1 week', revision: 'Follow-up', twoWeeks: '2 weeks',
      case3Title: 'Reconstruction after Implant Removal',
      case3Desc: 'Aesthetic reconstruction after removal of an old implant placed at another clinic twenty years earlier. The breast was falling in front of the encapsulated implant. The patient did not want more volume and the reconstruction was performed <strong>without implants</strong>.',
      withEncapsulated: 'With encapsulated implant', withoutProsthesis: 'Reconstruction without implants',
      case4Title: 'Ptosis and Asymmetry Correction',
      case4Desc: 'Ptosis and asymmetry after breastfeeding. Aesthetic correction with mastopexy.',
      case4Note: 'Appearance one month after surgery where the scars are visible and reddened during this period.',
      oneMonthAfter: '1 month later',
      case5Title: 'Aesthetic Volume Augmentation with Implants',
      case5Desc: 'Breast augmentation with implants to achieve natural and harmonious volume.',
      arrowBefore: '↑ Before', arrowAfter: '↓ After',
      case6Title: 'Mastopexy without Implants',
      case6Desc: 'Correction of breast ptosis using the patient\'s own tissues, without the need for implants.',
      case6Note: 'At 3 months the scars appear as fine red lines that gradually fade.',
      threeMonths: '3 months later',
      case7Title: 'Mastopexy without Implants - Tissue Repositioning',
      case7Desc: 'Technique that repositions the patient\'s own tissues to provide appropriate volume, without the need for implants.',
      case7Note: 'Natural result using exclusively the patient\'s own tissues.',
      case9Title: 'Breast Augmentation - Case 2',
      case9Desc: 'Surgical planning with preoperative marking and final result.',
      contactTitle: 'Interested in Breast Surgery?', contactDesc: 'Request a personalized assessment',
    },
    fr: {
      title: 'Chirurgie Esthétique Mammaire',
      heroP1: '<strong>Embellissement de la poitrine</strong> avec des prothèses anatomiques de dernière génération pour des résultats naturels et harmonieux.',
      heroP2: 'Consultation personnalisée pour choisir la technique et la taille les mieux adaptées à votre corps.',
      cta: 'DEMANDER UNE CONSULTATION',
      prosthesisTitle: 'Prothèses Anatomiques de Dernière Génération',
      naturalShape: 'Forme Naturelle', naturalShapeDesc: 'Elles imitent la forme naturelle du sein avec plus de volume dans la partie inférieure.',
      cohesiveGel: 'Gel Cohésif', cohesiveGelDesc: 'Remplissage en silicone haute cohésivité pour une sécurité accrue.',
      naturalTouch: 'Toucher Naturel', naturalTouchDesc: 'Texture et consistance très similaires au tissu mammaire naturel.',
      lifetimeWarranty: 'Garantie à Vie', lifetimeWarrantyDesc: 'Prothèses de grandes marques avec garantie à vie.',
      typesTitle: 'Types de Chirurgie Mammaire',
      augmentation: 'Augmentation Mammaire', augmentationDesc: 'Augmentation du volume mammaire par des prothèses en silicone (anatomiques ou rondes) pour obtenir une poitrine plus proportionnée et naturelle.',
      lift: 'Lifting des Seins (Mastopexie)', liftDesc: 'Correction de la ptose mammaire, en soulevant et remodelant le sein pour un aspect plus jeune et ferme.',
      reduction: 'Réduction Mammaire', reductionDesc: 'Diminution de la taille des seins en cas d\'hypertrophie mammaire, soulageant les problèmes de dos et améliorant la proportion corporelle.',
      expTitle: 'Expérience en Chirurgie Mammaire',
      expP1: 'Le Dr. Ángel Norro compte plus de <strong>30 ans d\'expérience</strong> en chirurgie esthétique mammaire, ayant réalisé des milliers d\'interventions.',
      expP2: 'Chaque patiente reçoit une <strong>étude personnalisée</strong> pour choisir le type de prothèse, la taille et la technique chirurgicale les plus appropriés.',
      expP3: 'L\'objectif est d\'obtenir un résultat <strong>naturel et harmonieux</strong> qui améliore la silhouette en respectant les proportions de chaque corps.',
      personalizedTitle: 'Résultats Personnalisés',
      personalizedP1: 'Les résultats sont toujours <strong>au goût de la patiente</strong> en termes de forme et de volume, toujours dans les limites des possibilités de chaque patiente, dont les circonstances particulières sont étudiées lors de la <strong>consultation préalable</strong>. Les images avant et après que nous montrons correspondent à des patientes satisfaites de leurs résultats, adaptés à leur <strong>goût personnel</strong>.',
      personalizedP2: 'Nous travaillons avec <strong>plus de 400 références de prothèses</strong> en termes de forme et de taille, ce qui nous permet de trouver l\'option parfaite pour chaque patiente.',
      personalizedP3: 'En <strong>chirurgie sans prothèse</strong>, nous pouvons concevoir la poitrine en forme et en taille entièrement au goût de la patiente, en utilisant des techniques de remodelage avec les propres tissus.',
      case1Title: 'Résultats avec Prothèses Anatomiques', case1Subtitle: 'Augmentation mammaire avec résultat naturel',
      frontalView: 'Vue Frontale', lateralViews: 'Vues Latérales', lateralView: 'Vue Latérale',
      before: 'Avant', after: 'Après',
      disclaimer: 'Cas réel - Les résultats peuvent varier selon chaque patiente',
      case2Title: 'Lifting et Augmentation Mammaire', case2Subtitle: 'Avant et une semaine lors du contrôle',
      oneWeek: '1 semaine', revision: 'Contrôle', twoWeeks: '2 semaines',
      case3Title: 'Reconstruction après Retrait de Prothèses',
      case3Desc: 'Reconstruction esthétique après retrait d\'une ancienne prothèse posée dans un autre service vingt ans auparavant. Le sein tombait devant la prothèse encapsulée. La patiente ne souhaitait pas plus de volume et la reconstruction a été réalisée <strong>sans prothèse</strong>.',
      withEncapsulated: 'Avec prothèse encapsulée', withoutProsthesis: 'Reconstruction sans prothèse',
      case4Title: 'Correction de Ptose et Asymétrie',
      case4Desc: 'Ptose et asymétrie après allaitements. Correction esthétique par mastopexie.',
      case4Note: 'Aspect un mois après la chirurgie où les cicatrices sont visibles et rougies pendant cette période.',
      oneMonthAfter: '1 mois après',
      case5Title: 'Augmentation de Volume Esthétique avec Prothèses',
      case5Desc: 'Augmentation mammaire avec prothèses pour obtenir un volume naturel et harmonieux.',
      arrowBefore: '↑ Avant', arrowAfter: '↓ Après',
      case6Title: 'Mastopexie sans Prothèse',
      case6Desc: 'Correction de la ptose mammaire en utilisant les propres tissus de la patiente, sans nécessité de prothèses.',
      case6Note: 'À 3 mois, les cicatrices apparaissent comme de fines lignes rouges qui s\'estompent progressivement.',
      threeMonths: '3 mois après',
      case7Title: 'Mastopexie sans Prothèse - Repositionnement des Tissus',
      case7Desc: 'Technique qui repositionne les propres tissus de la patiente pour apporter le volume approprié, sans nécessité d\'implants.',
      case7Note: 'Résultat naturel utilisant exclusivement les propres tissus.',
      case9Title: 'Augmentation Mammaire - Cas 2',
      case9Desc: 'Planification chirurgicale avec marquage préopératoire et résultat final.',
      contactTitle: 'Intéressée par la Chirurgie Mammaire?', contactDesc: 'Demandez une évaluation personnalisée',
    },
    pt: {
      title: 'Cirurgia Estética de Mamas',
      heroP1: '<strong>Embelezamento do peito</strong> com próteses anatômicas de última geração para resultados naturais e harmoniosos.',
      heroP2: 'Consulta personalizada para escolher a técnica e o tamanho que melhor se adapte ao seu corpo.',
      cta: 'SOLICITAR CONSULTA',
      prosthesisTitle: 'Próteses Anatômicas de Última Geração',
      naturalShape: 'Forma Natural', naturalShapeDesc: 'Imitam a forma natural da mama com mais volume na parte inferior.',
      cohesiveGel: 'Gel Coesivo', cohesiveGelDesc: 'Preenchimento de silicone de alta coesividade para maior segurança.',
      naturalTouch: 'Toque Natural', naturalTouchDesc: 'Textura e consistência muito similar ao tecido mamário natural.',
      lifetimeWarranty: 'Garantia Vitalícia', lifetimeWarrantyDesc: 'Próteses de primeiras marcas com garantia vitalícia.',
      typesTitle: 'Tipos de Cirurgia de Mamas',
      augmentation: 'Aumento de Peito', augmentationDesc: 'Aumento do volume mamário mediante próteses de silicone (anatômicas ou redondas) para conseguir um peito mais proporcionado e natural.',
      lift: 'Elevação de Peito (Mastopexia)', liftDesc: 'Correção da queda do peito, elevando e remodelando a mama para conseguir um aspecto mais jovem e firme.',
      reduction: 'Redução de Peito', reductionDesc: 'Diminuição do tamanho das mamas em casos de hipertrofia mamária, aliviando problemas de costas e melhorando a proporção corporal.',
      expTitle: 'Experiência em Cirurgia Mamária',
      expP1: 'O Dr. Ángel Norro conta com mais de <strong>30 anos de experiência</strong> em cirurgia estética de mamas, tendo realizado milhares de intervenções.',
      expP2: 'Cada paciente recebe um <strong>estudo personalizado</strong> para escolher o tipo de prótese, tamanho e técnica cirúrgica mais adequados.',
      expP3: 'O objetivo é conseguir um resultado <strong>natural e harmonioso</strong> que melhore a silhueta respeitando as proporções de cada corpo.',
      personalizedTitle: 'Resultados Personalizados',
      personalizedP1: 'Os resultados são sempre <strong>ao gosto da paciente</strong> em termos de forma e volume, sempre dentro dos limites das possibilidades de cada paciente, cujas circunstâncias particulares são estudadas na <strong>consulta prévia</strong>. As imagens de antes e depois que mostramos correspondem a pacientes satisfeitas com seus resultados, adaptados ao seu <strong>gosto pessoal</strong>.',
      personalizedP2: 'Trabalhamos com <strong>mais de 400 referências de próteses</strong> em termos de forma e tamanho, o que nos permite encontrar a opção perfeita para cada paciente.',
      personalizedP3: 'Em <strong>cirurgia sem prótese</strong>, podemos desenhar o peito em forma e tamanho completamente ao gosto da paciente, utilizando técnicas de remodelação com os próprios tecidos.',
      case1Title: 'Resultados com Próteses Anatômicas', case1Subtitle: 'Aumento de peito com resultado natural',
      frontalView: 'Vista Frontal', lateralViews: 'Vistas Laterais', lateralView: 'Vista Lateral',
      before: 'Antes', after: 'Depois',
      disclaimer: 'Caso real - Resultados podem variar segundo cada paciente',
      case2Title: 'Elevação e Aumento de Peito', case2Subtitle: 'Antes e uma semana na revisão',
      oneWeek: '1 semana', revision: 'Revisão', twoWeeks: '2 semanas',
      case3Title: 'Reconstrução após Retirada de Próteses',
      case3Desc: 'Reconstrução estética após retirada de prótese antiga colocada em outro serviço vinte anos antes. O peito caía pela frente da prótese encapsulada. A paciente não desejava mais volume e a reconstrução foi realizada <strong>sem prótese</strong>.',
      withEncapsulated: 'Com prótese encapsulada', withoutProsthesis: 'Reconstrução sem prótese',
      case4Title: 'Correção de Ptose e Assimetria',
      case4Desc: 'Ptose e assimetria após amamentações. Correção estética com mastopexia.',
      case4Note: 'Aspecto um mês após a cirurgia onde se apreciam as cicatrizes que neste período estão avermelhadas.',
      oneMonthAfter: '1 mês depois',
      case5Title: 'Aumento de Volume Estético com Próteses',
      case5Desc: 'Aumento mamário com próteses para conseguir um volume natural e harmônico.',
      arrowBefore: '↑ Antes', arrowAfter: '↓ Depois',
      case6Title: 'Mastopexia sem Prótese',
      case6Desc: 'Correção de ptose mamária utilizando os próprios tecidos da paciente, sem necessidade de próteses.',
      case6Note: 'Aos 3 meses apreciam-se as cicatrizes como finas linhas vermelhas que vão desaparecendo pouco a pouco.',
      threeMonths: '3 meses depois',
      case7Title: 'Mastopexia sem Prótese - Recolocação de Tecidos',
      case7Desc: 'Técnica que recoloca os próprios tecidos da paciente para aportar o volume apropriado, sem necessidade de implantes.',
      case7Note: 'Resultado natural utilizando exclusivamente os tecidos próprios.',
      case9Title: 'Aumento Mamário - Caso 2',
      case9Desc: 'Planejamento cirúrgico com marcação pré-operatória e resultado final.',
      contactTitle: 'Interessada em Cirurgia de Mamas?', contactDesc: 'Solicite uma avaliação personalizada',
    },
  };

  const texts = t[lang] || t.es;

  // Image groups for lightbox navigation
  const case1Imgs = [
    { src: '/images/mamas-pre-frontal.jpg', alt: texts.before, caption: `${texts.frontalView} - ${texts.before}` },
    { src: '/images/mamas-post-frontal.jpg', alt: texts.after, caption: `${texts.frontalView} - ${texts.after}` },
    { src: '/images/mamas-pre-lateral.jpg', alt: texts.before, caption: `${texts.lateralViews} - ${texts.before}` },
    { src: '/images/mamas-pre-lateral2.jpg', alt: texts.before, caption: `${texts.lateralViews} - ${texts.before}` },
    { src: '/images/mamas-post-lateral1.jpg', alt: texts.after, caption: `${texts.lateralViews} - ${texts.after}` },
    { src: '/images/mamas-post-lateral2.jpg', alt: texts.after, caption: `${texts.lateralViews} - ${texts.after}` },
  ];
  const case2Imgs = [
    { src: '/images/caso2-mamas-pre-frontal.jpg', alt: texts.before, caption: `${texts.frontalView} - ${texts.before}` },
    { src: '/images/caso2-mamas-post-frontal.jpg', alt: texts.oneWeek, caption: `${texts.frontalView} - ${texts.oneWeek}` },
    { src: '/images/caso2-mamas-pre-lateral.jpg', alt: texts.before, caption: `${texts.lateralView} - ${texts.before}` },
    { src: '/images/caso2-mamas-post-lateral.jpg', alt: texts.oneWeek, caption: `${texts.lateralView} - ${texts.oneWeek}` },
    { src: '/images/caso2-mamas-revision.jpg', alt: texts.revision, caption: `${texts.revision} - ${texts.twoWeeks}` },
  ];
  const case3Imgs = [
    { src: '/images/caso3-mamas-antes.jpg', alt: texts.before, caption: `${texts.before} - ${texts.withEncapsulated}` },
    { src: '/images/caso3-mamas-despues.jpg', alt: texts.after, caption: `${texts.after} - ${texts.withoutProsthesis}` },
  ];
  const case4Imgs = [
    { src: '/images/caso4-mamas-frontal.jpg', alt: texts.case4Title, caption: texts.frontalView },
    { src: '/images/caso4-mamas-lateral1.jpg', alt: texts.case4Title, caption: `${texts.lateralViews} 1` },
    { src: '/images/caso4-mamas-lateral2.jpg', alt: texts.case4Title, caption: `${texts.lateralViews} 2` },
  ];
  const case5Imgs = [
    { src: '/images/caso5-mamas-collage.jpg', alt: texts.case5Title, caption: texts.case5Title },
  ];
  const case6Imgs = [
    { src: '/images/caso6-pre-lateral.jpg', alt: texts.before, caption: `${texts.before} - 1` },
    { src: '/images/caso6-pre-frontal1.jpg', alt: texts.before, caption: `${texts.before} - 2` },
    { src: '/images/caso6-pre-frontal2.jpg', alt: texts.before, caption: `${texts.before} - 3` },
    { src: '/images/caso6-post-lateral1.jpg', alt: texts.threeMonths, caption: `${texts.threeMonths} - 1` },
    { src: '/images/caso6-post-frontal.jpg', alt: texts.threeMonths, caption: `${texts.threeMonths} - 2` },
    { src: '/images/caso6-post-lateral2.jpg', alt: texts.threeMonths, caption: `${texts.threeMonths} - 3` },
  ];
  const case7Imgs = [
    { src: '/images/mastopexia-sin-protesis-pre-lateral1.jpg', alt: texts.before, caption: `${texts.before} - 1` },
    { src: '/images/mastopexia-sin-protesis-pre-frontal.jpg', alt: texts.before, caption: `${texts.before} - 2` },
    { src: '/images/mastopexia-sin-protesis-pre-lateral2.jpg', alt: texts.before, caption: `${texts.before} - 3` },
    { src: '/images/mastopexia-sin-protesis-post-lateral1.jpg', alt: texts.after, caption: `${texts.after} - 1` },
    { src: '/images/mastopexia-sin-protesis-post-frontal.jpg', alt: texts.after, caption: `${texts.after} - 2` },
    { src: '/images/mastopexia-sin-protesis-post-lateral2.jpg', alt: texts.after, caption: `${texts.after} - 3` },
  ];
  const case9Imgs = [
    { src: '/images/mamoplastia-caso2-pre-lateral2.jpg', alt: texts.before, caption: texts.before },
    { src: '/images/mamoplastia-caso2-pre-lateral1.jpg', alt: texts.after, caption: texts.after },
  ];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#B8975A] to-[#8b7355]"></div>
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <a href="tel:952764547" className="text-[#8b7355] font-semibold hover:underline">952 76 45 47</a>
          </div>
          <h1 data-testid="mamas-title" className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1C1C1A] text-center mb-6 leading-tight">{texts.title}</h1>
          <p className="text-lg md:text-xl text-text-secondary text-center mb-4 max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: texts.heroP1 }} />
          <p className="text-lg text-text-secondary text-center mb-8">{texts.heroP2}</p>
          <div className="text-center">
            <Button data-testid="mamas-cta-button" onClick={() => scrollToSection('contacto')} className="bg-[#B8975A] hover:bg-[#a67259] text-white px-8 py-6 text-lg rounded transition-all duration-300">{texts.cta}</Button>
          </div>
        </div>
      </section>

      {/* Prótesis Anatómicas */}
      <section className="py-20 bg-[#1C1C1A] px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-8 text-center">{texts.prosthesisTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              [texts.naturalShape, texts.naturalShapeDesc],
              [texts.cohesiveGel, texts.cohesiveGelDesc],
              [texts.naturalTouch, texts.naturalTouchDesc],
              [texts.lifetimeWarranty, texts.lifetimeWarrantyDesc],
            ].map(([title, desc], i) => (
              <div key={i} className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
                <Check className="text-[#d4af37] mb-3" size={24} />
                <h3 className="text-white font-semibold mb-2">{title}</h3>
                <p className="text-white/80">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types */}
      <section className="py-20 px-4 bg-cream">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8">{texts.typesTitle}</h2>
          <div className="space-y-8">
            {[[texts.augmentation, texts.augmentationDesc],[texts.lift, texts.liftDesc],[texts.reduction, texts.reductionDesc]].map(([title, desc], i) => (
              <div key={i} className="bg-ivory rounded-none p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">{title}</h3>
                <p className="text-text-secondary">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doctor */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/3">
              <img src="/images/foto-angel-bata.webp" alt="Dr. Ángel Norro" className="w-full h-auto rounded-none shadow-lg" />
            </div>
            <div className="lg:w-2/3">
              <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-6">{texts.expTitle}</h2>
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: texts.expP1 }} />
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: texts.expP2 }} />
              <p className="text-text-secondary" dangerouslySetInnerHTML={{ __html: texts.expP3 }} />
            </div>
          </div>
        </div>
      </section>

      {/* Personalización */}
      <section className="py-16 bg-[#f8f5f2] px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] mb-6 text-center">{texts.personalizedTitle}</h2>
          <div className="bg-ivory rounded-none p-8 shadow-sm">
            <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: texts.personalizedP1 }} />
            <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: texts.personalizedP2 }} />
            <p className="text-text-secondary" dangerouslySetInnerHTML={{ __html: texts.personalizedP3 }} />
          </div>
        </div>
      </section>

      {/* CASE 1: Prótesis Anatómicas */}
      <section className="py-12 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-4">{texts.case1Title}</h2>
            <p className="text-center text-text-secondary mb-8">{texts.case1Subtitle}</p>
            
            <div className="mb-12">
              <h3 className="text-lg font-medium text-[#1C1C1A] text-center mb-4">{texts.frontalView}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {case1Imgs.slice(0, 2).map((img, i) => (
                  <div key={i} className="text-center">
                    <div className="border-4 border-[#B8975A] p-2 hover:border-[#a67259] transition-colors">
                      <ClickImg src={img.src} alt={img.alt} images={case1Imgs} index={i} onOpen={openLb} />
                    </div>
                    <p className="mt-3 text-text-secondary font-medium">{i === 0 ? texts.before : texts.after}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-8">
              <h3 className="text-lg font-medium text-[#1C1C1A] text-center mb-4">{texts.lateralViews}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
                {case1Imgs.slice(2).map((img, i) => (
                  <div key={i} className="text-center">
                    <div className="border-4 border-[#B8975A] p-2 hover:border-[#a67259] transition-colors">
                      <ClickImg src={img.src} alt={img.alt} images={case1Imgs} index={i + 2} onOpen={openLb} />
                    </div>
                    <p className="mt-3 text-text-secondary font-medium">{i < 2 ? texts.before : texts.after}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>

            {/* CASE 2: Mastopexia */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-4">{texts.case2Title}</h2>
              <p className="text-center text-text-secondary mb-8">{texts.case2Subtitle}</p>
              <div className="mb-8">
                <h3 className="text-lg font-medium text-[#1C1C1A] text-center mb-4">{texts.frontalView}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                  {case2Imgs.slice(0, 2).map((img, i) => (
                    <div key={i} className="text-center">
                      <div className="border-4 border-[#B8975A] p-2 hover:border-[#a67259] transition-colors">
                        <ClickImg src={img.src} alt={img.alt} images={case2Imgs} index={i} onOpen={openLb} />
                      </div>
                      <p className="mt-3 text-text-secondary font-medium">{i === 0 ? texts.before : texts.oneWeek}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mb-8">
                <h3 className="text-lg font-medium text-[#1C1C1A] text-center mb-4">{texts.lateralView}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                  {case2Imgs.slice(2, 4).map((img, i) => (
                    <div key={i} className="text-center">
                      <div className="border-4 border-[#B8975A] p-2 hover:border-[#a67259] transition-colors">
                        <ClickImg src={img.src} alt={img.alt} images={case2Imgs} index={i + 2} onOpen={openLb} />
                      </div>
                      <p className="mt-3 text-text-secondary font-medium">{i === 0 ? texts.before : texts.oneWeek}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-[#1C1C1A] text-center mb-4">{texts.revision}</h3>
                <div className="max-w-md mx-auto text-center">
                  <div className="border-4 border-[#B8975A] p-2 hover:border-[#a67259] transition-colors">
                    <ClickImg src={case2Imgs[4].src} alt={texts.revision} images={case2Imgs} index={4} onOpen={openLb} />
                  </div>
                  <p className="mt-3 text-text-secondary font-medium">{texts.twoWeeks}</p>
                </div>
              </div>
              <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
            </div>

            {/* CASE 3: Reconstrucción */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-4">{texts.case3Title}</h2>
              <p className="text-center text-text-secondary mb-8 max-w-2xl mx-auto" dangerouslySetInnerHTML={{ __html: texts.case3Desc }} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {case3Imgs.map((img, i) => (
                  <div key={i} className="text-center">
                    <div className="border-4 border-[#B8975A] p-2 hover:border-[#a67259] transition-colors">
                      <ClickImg src={img.src} alt={img.alt} images={case3Imgs} index={i} onOpen={openLb} />
                    </div>
                    <p className="mt-3 text-text-secondary font-medium">{i === 0 ? texts.before : texts.after}</p>
                    <p className="text-text-muted text-sm">{i === 0 ? texts.withEncapsulated : texts.withoutProsthesis}</p>
                  </div>
                ))}
              </div>
              <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
            </div>

            {/* CASE 4: Ptosis */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-4">{texts.case4Title}</h2>
              <p className="text-center text-text-secondary mb-4 max-w-2xl mx-auto">{texts.case4Desc}</p>
              <p className="text-center text-text-muted text-sm mb-8 max-w-2xl mx-auto"><em>{texts.case4Note}</em></p>
              <div className="mb-8">
                <h3 className="text-lg font-medium text-[#1C1C1A] text-center mb-4">{texts.frontalView}</h3>
                <div className="max-w-3xl mx-auto">
                  <div className="border-4 border-[#B8975A] p-2 hover:border-[#a67259] transition-colors">
                    <ClickImg src={case4Imgs[0].src} alt={case4Imgs[0].alt} images={case4Imgs} index={0} onOpen={openLb} />
                  </div>
                  <div className="flex justify-between mt-3 px-4">
                    <p className="text-text-secondary font-medium">{texts.oneMonthAfter}</p>
                    <p className="text-text-secondary font-medium">{texts.before}</p>
                  </div>
                </div>
              </div>
              <div className="mb-8">
                <h3 className="text-lg font-medium text-[#1C1C1A] text-center mb-4">{texts.lateralViews}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {case4Imgs.slice(1).map((img, i) => (
                    <div key={i}>
                      <div className="border-4 border-[#B8975A] p-2 hover:border-[#a67259] transition-colors">
                        <ClickImg src={img.src} alt={img.alt} images={case4Imgs} index={i + 1} onOpen={openLb} />
                      </div>
                      <div className="flex justify-between mt-3 px-4">
                        <p className="text-text-secondary font-medium text-sm">{texts.oneMonthAfter}</p>
                        <p className="text-text-secondary font-medium text-sm">{texts.before}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
            </div>

            {/* CASE 5: Volumen */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-4">{texts.case5Title}</h2>
              <p className="text-center text-text-secondary mb-8">{texts.case5Desc}</p>
              <div className="max-w-4xl mx-auto">
                <div className="border-4 border-[#B8975A] p-2 hover:border-[#a67259] transition-colors">
                  <ClickImg src={case5Imgs[0].src} alt={case5Imgs[0].alt} images={case5Imgs} index={0} onOpen={openLb} />
                </div>
                <div className="flex justify-center gap-12 mt-4">
                  <p className="text-text-secondary font-medium">{texts.arrowBefore}</p>
                  <p className="text-text-secondary font-medium">{texts.arrowAfter}</p>
                </div>
              </div>
              <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
            </div>

            {/* CASE 6: Mastopexia sin Prótesis */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-4">{texts.case6Title}</h2>
              <p className="text-center text-text-secondary mb-4 max-w-2xl mx-auto">{texts.case6Desc}</p>
              <p className="text-center text-text-muted text-sm mb-8 max-w-2xl mx-auto"><em>{texts.case6Note}</em></p>
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-3 gap-3 mb-2">
                  {case6Imgs.slice(0, 3).map((img, i) => (
                    <div key={i} className="aspect-[3/4] overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case6Imgs, i)}>
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-text-secondary font-medium mb-6">{texts.before}</p>
                <div className="grid grid-cols-3 gap-3 mb-2">
                  {case6Imgs.slice(3).map((img, i) => (
                    <div key={i} className="aspect-[3/4] overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case6Imgs, i + 3)}>
                      <img src={img.src} alt={img.alt} className={`w-full h-full object-cover ${i === 1 ? 'object-top' : 'object-center'}`} />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-text-secondary font-medium">{texts.threeMonths}</p>
              </div>
              <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
            </div>

            {/* CASE 7: Recolocación de Tejidos */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-4">{texts.case7Title}</h2>
              <p className="text-center text-text-secondary mb-4 max-w-2xl mx-auto">{texts.case7Desc}</p>
              <p className="text-center text-text-muted text-sm mb-8 max-w-2xl mx-auto"><em>{texts.case7Note}</em></p>
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-3 gap-3 mb-2">
                  {case7Imgs.slice(0, 3).map((img, i) => (
                    <div key={i} className="aspect-[3/4] overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case7Imgs, i)}>
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-text-secondary font-medium mb-6">{texts.before}</p>
                <div className="grid grid-cols-3 gap-3 mb-2">
                  {case7Imgs.slice(3).map((img, i) => (
                    <div key={i} className="aspect-[3/4] overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case7Imgs, i + 3)}>
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-text-secondary font-medium">{texts.after}</p>
              </div>
              <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CASE 9 */}
      <section className="py-12 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-4">{texts.case9Title}</h2>
            <p className="text-center text-text-secondary mb-8 max-w-2xl mx-auto">{texts.case9Desc}</p>
            <div className="max-w-3xl mx-auto">
              <div className="grid grid-cols-2 gap-4">
                {case9Imgs.map((img, i) => (
                  <div key={i} className="text-center">
                    <div className="aspect-[3/4] overflow-hidden rounded-none shadow-lg cursor-pointer group relative" onClick={() => openLb(case9Imgs, i)}>
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                      </div>
                    </div>
                    <p className="mt-3 text-text-secondary font-medium">{i === 0 ? texts.before : texts.after}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-center text-text-muted text-sm mt-6">{texts.disclaimer}</p>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contacto" className="py-20 bg-[#B8975A] px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">{texts.contactTitle}</h2>
          <p className="text-white/90 mb-8 text-lg">{texts.contactDesc}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:952764547" className="inline-flex items-center justify-center gap-2 bg-ivory text-[#B8975A] px-8 py-4 rounded-none font-semibold hover:bg-gray-100 transition-colors">
              <Phone size={20} /> 952 76 45 47
            </a>
            <a href="https://wa.me/34696345792" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-gold500 text-white px-8 py-4 rounded-none font-semibold hover:bg-gold600 transition-colors">
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />

      {/* Lightbox */}
      {lbIndex !== null && (
        <Lightbox
          images={lbImages}
          currentIndex={lbIndex}
          onClose={() => setLbIndex(null)}
          onPrev={() => setLbIndex(i => Math.max(0, i - 1))}
          onNext={() => setLbIndex(i => Math.min(lbImages.length - 1, i + 1))}
        />
      )}
    </div>
  );
};

export default CirugiaMamas;
